import { z } from 'zod';

import {
  ESTADOS_INCIDENCIA,
  MAX_FOTOS_INCIDENCIA,
  MAX_FOTOS_SUSTENTO_PRORROGA,
  MENSAJE_PDF_OBLIGATORIO_PLAZO_CURA,
  ORDENAR_POR_INCIDENCIA,
  ORDENES_INCIDENCIA,
  ORIGENES_INCIDENCIA,
} from './incidencia.entity';

const estadoEnum = z.enum(ESTADOS_INCIDENCIA as readonly [string, ...string[]]);
const origenEnum = z.enum(ORIGENES_INCIDENCIA as readonly [string, ...string[]]);
const ordenarPorEnum = z.enum(ORDENAR_POR_INCIDENCIA as readonly [string, ...string[]]);
const ordenEnum = z.enum(ORDENES_INCIDENCIA as readonly [string, ...string[]]);

/**
 * Fecha ISO `YYYY-MM-DD` (la que emite el DatePicker). `.catch(undefined)`
 * tolera un valor vacío o malformado en la URL sin romper la validación de la
 * ruta (igual criterio que `search`); el adapter omite el vacío del request.
 */
const fechaFiltroSchema = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

/** Coordenada string finita dentro de `[-limite, limite]` (rango WGS84). */
function esCoordenadaEnRango(valor: string, limite: 90 | 180): boolean {
  const n = Number(valor);
  return Number.isFinite(n) && n >= -limite && n <= limite;
}

/**
 * Filtros que acepta `GET /api/v1/incidencias`. El backend exige
 * `proyectoId` para acotar la consulta a la vista de detalle del
 * proyecto. `estado` opcional para los chips por estado del Seguimiento
 * y de la tab Incidencias.
 */
export const filtrosIncidenciasSchema = z
  .object({
    proyectoId: z.number().int().positive(),
    page: z.number().int().positive().optional(),
    limit: z.number().int().positive().max(100).optional(),
    estado: estadoEnum.optional(),
  })
  .strict();

export type FiltrosIncidenciasInput = z.infer<typeof filtrosIncidenciasSchema>;

/**
 * Filtros de la pantalla "Mis incidencias" (`GET /api/v1/incidencias` SIN
 * `proyectoId`). El alcance lo resuelve el backend por rol desde el JWT: un
 * Auxiliar recibe solo las que reportó; el Participante Privado líder, TODAS
 * las post-aprobación de sus proyectos asignados (ADR-047 — los líderes operan
 * en equipo; la vista propia se pide con `tomadaPorId`). Todos los filtros son
 * opcionales y viajan en el search param de la URL (compartible/navegable),
 * por eso `page`/`pageSize` usan `coerce` + `default` (los search params
 * llegan como string).
 *
 * `pageSize` es el nombre en la URL (coherente con usuarios/proyectos); el
 * adapter lo mapea a `limit` del backend.
 */
export const filtrosMisIncidenciasSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(10).max(100).default(20),
  // Sin `min(1)`: un `?search=` vacío en la URL no debe romper la validación;
  // el adapter omite los vacíos del request. El backend matchea contra el folio
  // (`codigo`) Y el código oficial (`codigoOficial`, ADR-043) — se puede pegar
  // cualquiera de los dos.
  search: z.string().trim().optional(),
  estados: z.array(estadoEnum).optional(),
  origen: origenEnum.optional(),
  proyectoId: z.coerce.number().int().positive().optional(),
  controlId: z.coerce.number().int().positive().optional(),
  includeInactive: z.boolean().optional(),
  // Filtros avanzados (búsqueda avanzada del listado). Rango de fecha de reporte
  // y de plazo (fechaLimite), ambos inclusive en el backend.
  reportadaDesde: fechaFiltroSchema,
  reportadaHasta: fechaFiltroSchema,
  fechaLimiteDesde: fechaFiltroSchema,
  fechaLimiteHasta: fechaFiltroSchema,
  // Plazo: vencidas (plazo activo con fechaLimite < ahora) y próximas a vencer
  // (fechaLimite entre hoy y hoy+N días). Mutuamente excluyentes en la UI.
  vencidas: z.boolean().optional().catch(undefined),
  porVencerEnDias: z.coerce.number().int().positive().max(365).optional().catch(undefined),
  // Por usuario (solo visible con permiso de lectura de usuarios).
  reportadaPorId: z.coerce.number().int().positive().optional(),
  tomadaPorId: z.coerce.number().int().positive().optional(),
  // Ordenamiento (campo + dirección). El backend defaultea reportadaEn/desc.
  ordenarPor: ordenarPorEnum.optional(),
  orden: ordenEnum.optional(),
  // Deep-link al detalle: abre el diálogo de esta incidencia al cargar la
  // página (lo usa la navegación de notificaciones del participante privado).
  // Es solo UI/routing —el adapter del request no lo envía al backend—.
  incidencia: z.coerce.number().int().positive().optional(),
});

export type FiltrosMisIncidenciasInput = z.infer<typeof filtrosMisIncidenciasSchema>;

/**
 * Validación del objeto de referencia de foto (`AttachFotoItem`) que se
 * envía en `fotos[]`. Refleja las cotas del contrato (longitudes y
 * mínimos). El `storageKey` lo provee `POST /storage/files`; el backend
 * valida además que el objeto exista.
 */
export const attachFotoItemSchema = z
  .object({
    storageKey: z.string().min(1).max(512),
    storageBucket: z.string().max(64).optional(),
    nombreOriginal: z.string().min(1).max(255),
    mimeType: z.string().min(1).max(100),
    tamanoBytes: z.number().int().min(1),
    hashSha256: z.string().max(64).optional(),
    anchoPx: z.number().int().min(1).optional(),
    altoPx: z.number().int().min(1).optional(),
    capturadaEn: z.string().datetime().optional(),
    dispositivo: z.string().max(200).optional(),
    // Ubicación de captura obligatoria por foto (ADR-026). String WGS84 validado
    // en rango: el backend rechaza fuera de rango con INCIDENCIA_COORDENADAS_INVALIDAS.
    latitudCaptura: z
      .string()
      .min(1, 'Falta la latitud de captura.')
      .refine((v) => esCoordenadaEnRango(v, 90), 'Latitud de captura fuera de rango.'),
    longitudCaptura: z
      .string()
      .min(1, 'Falta la longitud de captura.')
      .refine((v) => esCoordenadaEnRango(v, 180), 'Longitud de captura fuera de rango.'),
    clientUuid: z.string().uuid().optional(),
  })
  .strict();

export type AttachFotoItemInput = z.infer<typeof attachFotoItemSchema>;

/**
 * Body de `POST /api/v1/incidencias` desde la web (`CreateIncidenciaDto`).
 * Espeja las cotas del contrato (ADR-032):
 *
 *  - `controlId` requerido: id del `ControlProyecto` (condición/indicador
 *    unificados). Reemplaza el viejo XOR `condicionMinimaId`/`indicadorDesempenoId`.
 *  - `origen` fijo en `'Web'`. `deviceId` y `clientUuid` están PROHIBIDOS
 *    en web (los rechaza el backend con 400), por eso no figuran aquí.
 *  - `latitud`/`longitud` son un par: o van ambas o ninguna.
 *  - `fotos` son referencias ya subidas a storage (máx 10).
 */
export const crearIncidenciaSchema = z
  .object({
    proyectoId: z.number().int().positive(),
    /** Id del `ControlProyecto` reportado (ADR-032). */
    controlId: z.number().int().positive(),
    /**
     * Supuesto de incumplimiento del control reportado (ADR-031). Condicional:
     * obligatorio si el control tiene >1 supuesto activo; omitible si tiene 1
     * (el backend autoselecciona). El form lo resuelve con los supuestos del
     * control que viajan en el árbol del proyecto.
     */
    supuestoIncumplimientoId: z.number().int().positive().optional(),
    titulo: z.string().trim().min(1).max(200),
    descripcion: z.string().trim().min(1).max(4000),
    origen: z.literal('Web'),
    latitud: z.string().min(1).optional(),
    longitud: z.string().min(1).optional(),
    /**
     * Dirección textual del punto reportado (opcional, ≤500). Independiente de
     * las coordenadas; el form la omite si queda vacía tras el trim.
     */
    direccion: z
      .string()
      .trim()
      .min(1)
      .max(500, 'La dirección no puede superar 500 caracteres.')
      .optional(),
    fotos: z.array(attachFotoItemSchema).max(MAX_FOTOS_INCIDENCIA).optional(),
  })
  .strict()
  .refine((d) => (d.latitud == null) === (d.longitud == null), {
    message: 'Latitud y longitud deben enviarse juntas.',
    path: ['latitud'],
  });

export type CrearIncidenciaInput = z.infer<typeof crearIncidenciaSchema>;

/**
 * Validación de los formularios de transición que opera el supervisor desde el
 * detalle de la incidencia. `aprobar` y `rechazar` espejan el contrato real del
 * backend (paso 2 — revisión del supervisor). Las demás (`ampliar`) siguen
 * PROVISIONALES hasta que el backend cierre su contrato.
 */

/**
 * Body de `POST /api/v1/incidencias/:id/aprobar` (`ApproveIncidenciaDto`).
 *
 * ADR-031 (ROMPEDOR): `fechaLimite` deja de ser siempre obligatoria — es
 * **condicional al modo del supuesto** sellado en la incidencia
 * (`tipoPlazoSnap`):
 *  - `Fijo`       → NO enviar; el backend la deriva (`aprobadaEn + plazo`).
 *  - `PorEstudio` → REQUERIDA (el Supervisor la fija a mano).
 *  - `SinCura`    → NO enviar; al aprobar la incidencia queda `Registrada`.
 *  - legacy (snapshot null) → REQUERIDA.
 *
 * El schema deja ambos campos opcionales; la condicionalidad la decide el
 * diálogo según `tipoPlazoSnap` y la enforza el backend
 * (`INCIDENCIA_FECHA_LIMITE_INVALIDA`). `supuestoIncumplimientoId` permite
 * al Supervisor corregir el supuesto antes de sellarlo (opcional).
 *
 * Cambio E9: ya **no** se envía `participantePrivadoEmail`.
 */
export const aprobarIncidenciaSchema = z
  .object({
    fechaLimite: z.string().datetime({ message: 'Seleccione una fecha límite.' }).optional(),
    supuestoIncumplimientoId: z.number().int().positive().optional(),
    // Plazo de cura MANUAL para supuestos SinCura: el Supervisor puede dar una
    // ventana de corrección a un SinCura (→ pasa a `Abierta`). Pareja (ambos o
    // ninguno) y prohibidos junto con `fechaLimite`; el diálogo lo enforza y el
    // backend revalida (`PLAZO_MANUAL_INCOMPLETO`/`NO_MANUAL_EN_MODO_DERIVADO`).
    plazoCuraValor: z.number().int().positive().optional(),
    plazoCuraUnidad: z.enum(['Minutos', 'Horas', 'DiasCalendario']).optional(),
  })
  .strict();

export type AprobarIncidenciaInput = z.infer<typeof aprobarIncidenciaSchema>;

/**
 * Body de `POST /api/v1/incidencias/:id/rechazar` (`RejectIncidenciaConFotosDto`).
 * `motivo` obligatorio, 1–1000 caracteres. `fotosObservadasIds` (ADR-039,
 * opcional): ids de fotos de `Reporte` vigentes que el Supervisor marca como
 * observadas en este rechazo — quedan selladas al rechazo (el participante
 * privado deja de verlas) y el correo al reportante incluye su galería.
 * Omitir (o `[]`) cuando el rechazo no observa fotos. Ids no sellables →
 * 400 `INCIDENCIA_FOTO_OBSERVADA_INVALIDA`.
 */
export const rechazarIncidenciaSchema = z
  .object({
    motivo: z
      .string()
      .trim()
      .min(1, 'Indique el motivo del rechazo.')
      .max(1000, 'El motivo no puede superar 1000 caracteres.'),
    fotosObservadasIds: z.array(z.number().int().positive()).optional(),
  })
  .strict();

export type RechazarIncidenciaInput = z.infer<typeof rechazarIncidenciaSchema>;

/**
 * Otorgamiento de una prórroga (contrato prórrogas §3): el plazo se concede en
 * **exactamente una** de dos formas. Aplica igual a la ampliación directa
 * (`RegisterAmpliacionDto`) y a la autorización de una solicitud
 * (`AutorizarAmpliacionDto`); solo cambia el campo de texto opcional.
 *
 *  - **Forma A — plazo numérico** (recomendada, regla B5): `plazoValor` (entero
 *    ≥1) + `plazoUnidad`. El backend calcula la nueva fecha desde el plazo
 *    vigente (o desde ahora si ya venció).
 *  - **Forma B — fecha + hora exacta**: `fechaLimiteNueva` (ISO-8601 con hora).
 *
 * Reglas (las revalida el backend → `INCIDENCIA_FECHA_LIMITE_INVALIDA` con
 * `metadata.motivo`):
 *  - Enviar ambas formas → `AMPLIACION_ENTRADA_AMBIGUA`.
 *  - No enviar ninguna, o un plazo numérico incompleto → `AMPLIACION_ENTRADA_REQUERIDA`.
 */
const UNIDADES_PLAZO = ['Minutos', 'Horas', 'DiasCalendario'] as const;

const otorgarPlazoFields = {
  plazoValor: z.number().int().positive().optional(),
  plazoUnidad: z.enum(UNIDADES_PLAZO).optional(),
  fechaLimiteNueva: z
    .string()
    .datetime({ message: 'Seleccione la nueva fecha límite.' })
    .optional(),
};

/** Enforza "exactamente una forma" (plazo numérico completo XOR fecha exacta). */
function refinarOtorgarPlazo(
  d: {
    plazoValor?: number | undefined;
    plazoUnidad?: string | undefined;
    fechaLimiteNueva?: string | undefined;
  },
  ctx: z.RefinementCtx,
): void {
  const tienePlazo = d.plazoValor != null || d.plazoUnidad != null;
  const plazoCompleto = d.plazoValor != null && d.plazoUnidad != null;
  const tieneFecha = d.fechaLimiteNueva != null;

  if (tienePlazo && tieneFecha) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Elija una sola forma: plazo o fecha exacta.',
      path: ['plazoValor'],
    });
    return;
  }
  if (!tienePlazo && !tieneFecha) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Indique un plazo o una fecha exacta.',
      path: ['plazoValor'],
    });
    return;
  }
  if (tienePlazo && !plazoCompleto) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Indique la cantidad y la unidad del plazo.',
      path: d.plazoValor == null ? ['plazoValor'] : ['plazoUnidad'],
    });
  }
}

/**
 * Body de `POST /api/v1/incidencias/:id/ampliaciones` (`RegisterAmpliacionDto`).
 * Otorgamiento directo (Admin/SuperAdmin) en una de las dos formas; `motivo`
 * opcional (≤1000).
 */
export const ampliarIncidenciaSchema = z
  .object({
    ...otorgarPlazoFields,
    motivo: z.string().trim().max(1000, 'El motivo no puede superar 1000 caracteres.').optional(),
  })
  .strict()
  .superRefine(refinarOtorgarPlazo);

export type AmpliarIncidenciaInput = z.infer<typeof ampliarIncidenciaSchema>;

/**
 * Body de `POST /api/v1/incidencias/:id/reportar-terminada` (`ReportarTerminadaDto`).
 * Lo envía el participante privado (líder o auxiliar) al concluir el trabajo.
 *  - `comentario`: requerido, ≤2000.
 *  - `fechaFinTrabajo`: ISO-8601. El plazo ya NO la acota (ADR-051): reportar
 *    nunca se bloquea por plazo, el backend anota si se hizo en plazo
 *    (`plazoReporteTerminado`). El date picker la limita al más tardío entre
 *    el plazo vigente (dentro de él se admite una fecha futura) y hoy.
 *
 * Además del body, la incidencia debe tener **≥1 foto activa** subida antes
 * (`/incidencias/:id/fotos`); si no, 400 `INCIDENCIA_SIN_EVIDENCIA`. Esa regla
 * se valida en la UI antes de enviar (no es parte del body).
 */
export const reportarTerminadaSchema = z
  .object({
    comentario: z
      .string()
      .trim()
      .min(1, 'Describa el trabajo realizado.')
      .max(2000, 'El comentario no puede superar 2000 caracteres.'),
    fechaFinTrabajo: z.string().datetime({ message: 'Seleccione la fecha de fin del trabajo.' }),
  })
  .strict();

export type ReportarTerminadaInput = z.infer<typeof reportarTerminadaSchema>;

/**
 * Body de `POST /api/v1/incidencias/:id/aprobar-cumplimiento` ·
 * `incumplimiento` (`DictamenDto`, ADR-025). El Supervisor adjunta su
 * **evidencia del dictamen** inline:
 *  - `comentario`: requerido, ≤2000.
 *  - `fotos`: **1..10** referencias ya subidas a `/storage/files`; el backend
 *    les fuerza `tipoFoto='Dictamen'` y tienen cupo propio (no consumen la
 *    galería del PP). Sin foto → 400 `INCIDENCIA_DICTAMEN_SIN_EVIDENCIA`; sin
 *    comentario → 400 `INCIDENCIA_DICTAMEN_SIN_COMENTARIO`.
 *
 * El rechazo de cumplimiento usa `dictamenRechazoSchema` (evidencia opcional).
 * El contexto offline (`origen`/`deviceId`/`clientUuid`) lo omite la web.
 *
 * ADR-044: ambos dictámenes aceptan `verificacionCampoId` — el id de una
 * verificación de campo `Pendiente` del Auxiliar que el dictamen consume como
 * evidencia. Si viene, las fotos propias del Supervisor pasan a ser opcionales
 * (la evidencia es la del Auxiliar); sin él, ADR-025 queda intacto (≥1 foto al
 * aprobar). Verificación ya resuelta → 409 `INCIDENCIA_VERIFICACION_NO_PENDIENTE`.
 */
const comentarioDictamenSchema = z
  .string()
  .trim()
  .min(1, 'Indique un comentario del dictamen.')
  .max(2000, 'El comentario no puede superar 2000 caracteres.');

/**
 * Bloque `prorroga` de los dictámenes de cumplimiento (ADR-021 ter): resuelve
 * la solicitud de prórroga `Pendiente` en la misma transacción del dictamen —
 * siempre **como constancia** (nunca mueve `fechaLimite`). OBLIGATORIO en
 * `aprobar-cumplimiento` cuando hay solicitud pendiente (cerrar sin resolverla
 * la dejaría huérfana → 400 `INCIDENCIA_SOLICITUD_AMPLIACION_PENDIENTE_AL_CERRAR`);
 * opcional en `rechazar-cumplimiento` (la incidencia vuelve a `EnAtencion`,
 * donde la solicitud sigue accionable por el endpoint dedicado).
 */
export const prorrogaDecisionSchema = z
  .object({
    decision: z.enum(['Aprobar', 'Rechazar']),
    comentario: z
      .string()
      .trim()
      .max(1000, 'El comentario no puede superar 1000 caracteres.')
      .optional(),
  })
  .strict();

export type ProrrogaDecisionInput = z.infer<typeof prorrogaDecisionSchema>;

export const dictamenSchema = z
  .object({
    comentario: comentarioDictamenSchema,
    fotos: z
      .array(attachFotoItemSchema)
      .max(MAX_FOTOS_INCIDENCIA, `Máximo ${MAX_FOTOS_INCIDENCIA} fotos por dictamen.`),
    verificacionCampoId: z.number().int().positive().optional(),
    prorroga: prorrogaDecisionSchema.optional(),
  })
  .strict()
  .refine((d) => d.verificacionCampoId != null || d.fotos.length > 0, {
    message: 'Adjunte al menos una foto de la verificación.',
    path: ['fotos'],
  });

export type DictamenInput = z.infer<typeof dictamenSchema>;

/**
 * Body de `POST /api/v1/incidencias/:id/rechazar-cumplimiento`
 * (`DictamenRechazoDto`, enmienda ADR-025): igual que `dictamenSchema` pero la
 * evidencia fotográfica es **opcional** (0..10) — el motivo puede bastar para
 * devolver el trabajo a atención; la evidencia del PP ya documenta el estado.
 * También puede consumir una verificación de campo (`verificacionCampoId`,
 * ADR-044): ambas salidas del dictamen la consumen.
 */
export const dictamenRechazoSchema = z
  .object({
    comentario: comentarioDictamenSchema,
    fotos: z
      .array(attachFotoItemSchema)
      .max(MAX_FOTOS_INCIDENCIA, `Máximo ${MAX_FOTOS_INCIDENCIA} fotos por dictamen.`),
    verificacionCampoId: z.number().int().positive().optional(),
    prorroga: prorrogaDecisionSchema.optional(),
  })
  .strict();

export type DictamenRechazoInput = z.infer<typeof dictamenRechazoSchema>;

/**
 * Body de `POST /api/v1/incidencias/:id/verificaciones-campo`
 * (`VerificacionCampoDto`, ADR-044). El Auxiliar del proyecto registra lo
 * constatado en campo con la incidencia `EnVerificacion`:
 *  - `comentario`: requerido, ≤2000.
 *  - `fotos`: **1..10** referencias ya subidas a `/storage/files`, con
 *    ubicación de captura obligatoria (ADR-026); el backend les fuerza
 *    `tipoFoto='VerificacionCampo'` y las sella con marca de agua (ADR-041).
 *    Sin foto → 400 `INCIDENCIA_VERIFICACION_SIN_EVIDENCIA`.
 *
 * Solo una `Pendiente` a la vez → 409
 * `INCIDENCIA_VERIFICACION_PENDIENTE_DUPLICADA`; incidencia fuera de
 * `EnVerificacion` → 409 `INCIDENCIA_NO_ADMITE_VERIFICACION_CAMPO`. El
 * contexto offline (`origen`/`deviceId`/`clientUuid`) lo omite la web.
 */
export const verificacionCampoSchema = z
  .object({
    comentario: z
      .string()
      .trim()
      .min(1, 'Describa lo constatado en campo.')
      .max(2000, 'El comentario no puede superar 2000 caracteres.'),
    fotos: z
      .array(attachFotoItemSchema)
      .min(1, 'Adjunte al menos una foto de lo constatado.')
      .max(MAX_FOTOS_INCIDENCIA, `Máximo ${MAX_FOTOS_INCIDENCIA} fotos por verificación.`),
  })
  .strict();

export type VerificacionCampoInput = z.infer<typeof verificacionCampoSchema>;

/**
 * Body de `POST /api/v1/incidencias/:id/verificaciones-campo/:vid/observar`
 * (`ObservarVerificacionCampoDto`, ADR-044). El Supervisor devuelve la
 * verificación `Pendiente` al Auxiliar con motivo (→ `Observada`); la
 * incidencia no se mueve y el PP no se entera. `estadoEsperado` (precondición
 * de frescura B2, sobre el estado de la VERIFICACIÓN) lo omite la web.
 */
export const observarVerificacionCampoSchema = z
  .object({
    motivo: z
      .string()
      .trim()
      .min(1, 'Indique qué debe corregir el auxiliar.')
      .max(1000, 'El motivo no puede superar 1000 caracteres.'),
  })
  .strict();

export type ObservarVerificacionCampoInput = z.infer<typeof observarVerificacionCampoSchema>;

/**
 * Body de `POST /api/v1/incidencias/:id/seguimientos` (`RegistrarSeguimientoDto`,
 * ADR-045). El equipo interno de ANI (Auxiliar o Supervisor del proyecto) deja
 * constancia de una **visita de campo** mientras el participante privado
 * atiende la incidencia. No transiciona nada: es histórico.
 *
 *  - `comentario`: requerido, ≤2000.
 *  - `fotos`: **1..10** referencias ya subidas a `/storage/files`, con ubicación
 *    de captura obligatoria por foto (ADR-026); el backend les fuerza
 *    `tipoFoto='Seguimiento'` y las sella con marca de agua (ADR-041).
 *  - `latitud`/`longitud`: **obligatorias** — dónde estaba quien registra. Son
 *    OTRO punto que el de cada foto (`latitudCaptura`): se puede fotografiar un
 *    tramo desde 50 m. La web las fija con el mismo punto que las fotos (ver
 *    `RegistrarAvanceDialog`), que es el caso real dominante.
 *  - `direccion`: opcional, ≤500.
 *  - `capturadoEn`: opcional — instante REAL de la visita. **No puede ser
 *    futuro** (400 `INCIDENCIA_SEGUIMIENTO_FECHA_CAPTURA_INVALIDA`); omitirlo
 *    hace que el backend use el momento de la petición. El histórico ordena por
 *    este campo, así que registrar hoy la visita de ayer conserva la cronología.
 *
 * Fuera de la ventana de estados viva → 409 `INCIDENCIA_NO_ADMITE_SEGUIMIENTO`
 * (definitivo). El contexto offline (`origen`/`deviceId`/`clientUuid`) lo omite
 * la web: es exclusivo del batch `/sync-seguimientos` del móvil.
 */
export const registrarSeguimientoSchema = z
  .object({
    comentario: z
      .string()
      .trim()
      .min(1, 'Describa lo constatado en la visita.')
      .max(2000, 'El comentario no puede superar 2000 caracteres.'),
    latitud: z
      .string()
      .min(1, 'Falta la latitud de la visita.')
      .refine((v) => esCoordenadaEnRango(v, 90), 'Latitud de la visita fuera de rango.'),
    longitud: z
      .string()
      .min(1, 'Falta la longitud de la visita.')
      .refine((v) => esCoordenadaEnRango(v, 180), 'Longitud de la visita fuera de rango.'),
    direccion: z
      .string()
      .trim()
      .min(1)
      .max(500, 'La dirección no puede superar 500 caracteres.')
      .optional(),
    capturadoEn: z.string().datetime().optional(),
    fotos: z
      .array(attachFotoItemSchema)
      .min(1, 'Adjunte al menos una foto de la visita.')
      .max(MAX_FOTOS_INCIDENCIA, `Máximo ${MAX_FOTOS_INCIDENCIA} fotos por avance.`),
  })
  .strict();

export type RegistrarSeguimientoInput = z.infer<typeof registrarSeguimientoSchema>;

/**
 * Body de `POST /api/v1/incidencias/:id/ampliacion-solicitudes`
 * (`SolicitarAmpliacionDto`). Lo envía el PP líder para pedir un plazo de cura
 * con su **sustento: el PDF es obligatorio y las fotos lo complementan**
 * (regla de negocio sobre ADR-050, que en el backend admite fotos solas):
 *  - `motivo`: requerido, ≤1000.
 *  - `documentoStorageKey`: requerido, un único PDF de sustento (≤2 MB) ya
 *    subido a `/storage/files` (bucket `documentos-incidencias`);
 *    `documentoStorageBucket` opcional.
 *  - `fotos`: opcionales, 1..5 referencias ya subidas (mismo `AttachFotoItem`
 *    del resto de evidencia, con ubicación de captura obligatoria, ADR-026). El backend les
 *    fuerza `tipoFoto='SustentoProrroga'` y las sella con marca de agua.
 *  - `fechaLimiteSugerida`: ISO-8601 (el supervisor puede diferir al autorizar).
 *
 * Sin PDF se corta en el cliente aunque haya fotos (el backend solo da 400
 * `INCIDENCIA_SOLICITUD_AMPLIACION_SUSTENTO_INVALIDO` sin ningún sustento);
 * >5 fotos → 400 `INCIDENCIA_SOLICITUD_AMPLIACION_FOTOS_LIMITE_EXCEDIDO`; PDF >2 MB → 400
 * `INCIDENCIA_SOLICITUD_AMPLIACION_DOCUMENTO_DEMASIADO_GRANDE`. Solo una
 * solicitud `Pendiente` a la vez → 409 `SOLICITUD_AMPLIACION_DUPLICADA`.
 */
export const solicitarAmpliacionSchema = z
  .object({
    motivo: z
      .string()
      .trim()
      .min(1, 'Indique el motivo de la solicitud de plazo de cura.')
      .max(1000, 'El motivo no puede superar 1000 caracteres.'),
    documentoStorageKey: z
      .string({ error: MENSAJE_PDF_OBLIGATORIO_PLAZO_CURA })
      .min(1, MENSAJE_PDF_OBLIGATORIO_PLAZO_CURA)
      .max(512),
    documentoStorageBucket: z.string().max(64).optional(),
    fotos: z
      .array(attachFotoItemSchema)
      .min(1, 'Adjunte al menos una foto de sustento.')
      .max(MAX_FOTOS_SUSTENTO_PRORROGA, `Máximo ${MAX_FOTOS_SUSTENTO_PRORROGA} fotos de sustento.`)
      .optional(),
    fechaLimiteSugerida: z.string().datetime({ message: 'Seleccione la fecha sugerida.' }),
  })
  .strict();

export type SolicitarAmpliacionInput = z.infer<typeof solicitarAmpliacionSchema>;

/**
 * Body de `POST /api/v1/incidencias/:id/ampliacion-solicitudes/:sid/autorizar`
 * (`AutorizarAmpliacionDto`). El supervisor aprueba la prórroga y otorga el plazo
 * en una de las dos formas (igual que `ampliarIncidenciaSchema`), pudiendo diferir
 * de lo sugerido por el participante.
 *  - Forma A `{ plazoValor, plazoUnidad }` o Forma B `{ fechaLimiteNueva }`.
 *  - `comentario`: opcional, ≤1000.
 *
 * ADR-021 ter: la regla "exactamente una forma" solo aplica cuando la
 * incidencia sigue con plazo activo (`Abierta`/`EnAtencion`/`EnIncumplimiento`).
 * Si el PP ya reportó terminado, la aprobación es **declarativa** (constancia,
 * sin mover el plazo) y el body solo lleva el comentario — usar
 * `autorizarAmpliacionConstanciaSchema`.
 */
export const autorizarAmpliacionSchema = z
  .object({
    ...otorgarPlazoFields,
    comentario: z
      .string()
      .trim()
      .max(1000, 'El comentario no puede superar 1000 caracteres.')
      .optional(),
  })
  .strict()
  .superRefine(refinarOtorgarPlazo);

export type AutorizarAmpliacionInput = z.infer<typeof autorizarAmpliacionSchema>;

/**
 * Variante declarativa de la autorización (ADR-021 ter): el PP ya reportó
 * terminado (o la incidencia llegó a un estado terminal), así que no hay
 * `fechaLimite` que mover — la solicitud queda `Aprobada` como constancia
 * (`aplicadaAFechaLimite: false`) y solo viaja el comentario opcional.
 */
export const autorizarAmpliacionConstanciaSchema = z
  .object({
    comentario: z
      .string()
      .trim()
      .max(1000, 'El comentario no puede superar 1000 caracteres.')
      .optional(),
  })
  .strict();

/**
 * Body de `PATCH /api/v1/incidencias/:id` (`UpdateIncidenciaDto`). Edición
 * parcial de título/descripción/ubicación —corrección del reportante previa a
 * reenviar tras un rechazo—; no transiciona el estado. En el contrato los campos
 * son opcionales (omitir = no cambiar); el formulario precarga y reenvía título
 * y descripción (por eso aquí se exigen no vacíos; un `""` da 400). La ubicación
 * es opcional y **pareada** (ambas o ninguna; omitirlas deja la ubicación
 * intacta — el contrato no permite "limpiarla"). La `direccion` sí es
 * limpiable: string = nuevo valor, `null` = limpiar, omitir = no tocar.
 */
export const editarIncidenciaSchema = z
  .object({
    titulo: z
      .string()
      .trim()
      .min(1, 'Ingrese un título.')
      .max(200, 'El título no puede superar 200 caracteres.'),
    descripcion: z
      .string()
      .trim()
      .min(1, 'Ingrese una descripción.')
      .max(4000, 'La descripción no puede superar 4000 caracteres.'),
    latitud: z.string().min(1).optional(),
    longitud: z.string().min(1).optional(),
    direccion: z
      .string()
      .trim()
      .max(500, 'La dirección no puede superar 500 caracteres.')
      .nullable()
      .optional(),
  })
  .strict()
  .refine((d) => (d.latitud == null) === (d.longitud == null), {
    message: 'Latitud y longitud deben enviarse juntas.',
    path: ['latitud'],
  });

export type EditarIncidenciaInput = z.infer<typeof editarIncidenciaSchema>;
